import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import './ExamPrep.css';
import './JapaneseExamPrep.css';
import './JapaneseExamQuestions.css';

// Section display names (JLPT). These are the only thing that differs from the
// standard Exam Prep result page — the layout/classes are identical (ep-result-*).
const SECTION_DISPLAY = {
  vocab: 'Character & Vocabulary',
  grammar_reading: 'Grammar & Reading',
  listening: 'Listening',
};
const SECTION_NOTE = {
  vocab: 'Identify key vocabulary and characters more confidently.',
  grammar_reading: 'Strengthen grammar patterns and reading comprehension.',
  listening: 'Focus on sentence structure and everyday conversation.',
};
const SECTION_ORDER = ['vocab', 'grammar_reading', 'listening'];
const SECTION_PASS = 19; // pass mark per section (out of 60)

const JapaneseExamResult = ({ user, levelCode, attemptId, result, sectionMeta, onReady, onExit }) => {
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';
  const [tab, setTab] = useState('incorrect');
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [feedback, setFeedback] = useState((result && result.feedback) || null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/japanese-exam/attempt/${attemptId}`, { withCredentials: true });
        if (cancelled) return;
        if (res.data && res.data.success) {
          setAttempt(res.data.attempt);
          setAnswers(res.data.answers || []);
          if (res.data.feedback) setFeedback(res.data.feedback);
        }
      } catch (err) {
        console.error('Load attempt failed:', err.response?.data || err.message);
      } finally {
        if (!cancelled) { setLoading(false); if (onReady) onReady(); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, attemptId]);

  if (loading || !attempt) {
    return (
      <div className="exam-prep-page ep-result-page jeq-result-page">
        <Header user={user} />
        <SideNav />
        <main className="ep-result-main"><div className="jeq-loading">Scoring your exam…</div></main>
        <Footer />
      </div>
    );
  }

  const num = (v) => (v == null ? 0 : Number(v));
  const overallScore = num(attempt.score);
  const maxScore = num(attempt.max_score) || 180;
  const passed = attempt.passed === true || attempt.passed === 't';
  const statusLabel = passed ? 'Passed' : 'Failed';
  const scorePct = maxScore > 0 ? Math.round((overallScore / maxScore) * 100) : 0;
  const totalTime = num(attempt.time_taken);
  const timeMins = Math.floor(totalTime / 60);
  const timeSecs = totalTime % 60;

  const secName = (k) => (sectionMeta?.[k]?.shortName) || SECTION_DISPLAY[k];

  // Section-wise rows: Section / Performance / Score per section (out of 60).
  const sectionScores = SECTION_ORDER.map((k) => {
    const raw = k === 'vocab' ? num(attempt.sec_a_score)
      : k === 'grammar_reading' ? num(attempt.sec_b_score)
      : num(attempt.sec_c_score);
    return { key: k, section: secName(k), score: raw, max: 60, pct: Math.round((raw / 60) * 100) };
  });

  // Feedback (headline / summary / highlights / keep-going) with graceful defaults.
  const headline = (feedback && feedback.headline) || (passed ? "Well done! You've completed your mock test." : 'You have completed your mock test.');
  const summary = (feedback && feedback.summary) || "Here's a summary of your performance. Your overall score reflects how you did across the three sections.";
  const highlightsTitle = (feedback && feedback.highlights && feedback.highlights.section_title) || 'Your progress';
  const highlights = (feedback && feedback.highlights && Array.isArray(feedback.highlights.items) && feedback.highlights.items.length)
    ? feedback.highlights.items.map((it) => ({ type: it.type, title: it.label || it.section || it.title || '', message: it.message || it.note || it.text || '' }))
    : SECTION_ORDER.map((k) => ({ type: 'info', title: SECTION_DISPLAY[k], message: SECTION_NOTE[k] }));
  const keepGoing = (feedback && feedback.keep_going_message) || "You're getting closer to the next proficiency level. Continue practicing to strengthen your skills.";

  const R = 52;
  const C = 2 * Math.PI * R; // ≈ 327

  const isCorrect = (a) => a.is_correct === true || a.is_correct === 't';
  const isSkipped = (a) => a.is_skipped === true || a.is_skipped === 't' || a.user_answer === '__skipped__';
  const incorrect = answers.filter((a) => !isSkipped(a) && a.correct_answer != null && !isCorrect(a));
  const skipped = answers.filter(isSkipped);
  const activeReview = tab === 'incorrect' ? incorrect : skipped;

  const optText = (a, n) => {
    if (n == null) return '';
    let opts = a.options;
    if (typeof opts === 'string') { try { opts = JSON.parse(opts); } catch (e) { opts = []; } }
    const o = (opts || []).find((x) => Number(x.num) === Number(n));
    return o ? `${n}. ${o.text}` : `${n}`;
  };

  // Group review items by section (one heading per section).
  const bySection = [];
  activeReview.forEach((r) => {
    let g = bySection.find((x) => x.key === r.section_key);
    if (!g) { g = { key: r.section_key, section: secName(r.section_key) || SECTION_DISPLAY[r.section_key] || r.section_key, items: [] }; bySection.push(g); }
    g.items.push(r);
  });
  bySection.sort((a, b) => SECTION_ORDER.indexOf(a.key) - SECTION_ORDER.indexOf(b.key));

  const flatNum = (a) => answers.findIndex((x) => x.item_id === a.item_id) + 1;

  return (
    <div className="exam-prep-page ep-result-page jeq-result-page">
      <Header user={user} />
      <SideNav />

      <main className="ep-result-main">
        <h1 className="ep-result-title">
          <span className="ep-result-title-purple">Exam Prep</span> Result - Japanese
          <img src="/images/flag-japanese.svg" alt="" width="22" height="22" style={{ verticalAlign: 'middle', marginLeft: '6px' }} onError={(e) => { e.target.style.display = 'none'; }} />
        </h1>

        {/* Progress result card */}
        <div className="ep-result-card">
          <div className="ep-result-card-top">
            <span className="ep-result-card-title">
              <img src="/images/Star-black.svg" alt="" width="18" height="18" onError={(e) => { e.target.style.display = 'none'; }} /> Progress result
              <span className="ep-result-date-divider"></span>
              <span className="ep-result-date">{new Date(attempt.completed_at || Date.now()).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            </span>
            <span className="ep-result-time">
              <img src="/images/Info.svg" alt="" width="14" height="14" onError={(e) => { e.target.style.display = 'none'; }} /> Completed in <strong>{timeMins} minutes {timeSecs} seconds</strong>
            </span>
          </div>
          <div className="ep-result-body">
            <div className="ep-result-score-box">
              <div className="ep-result-score-circle">
                <svg viewBox="0 0 120 120" className="ep-score-svg">
                  <circle cx="60" cy="60" r={R} fill="none" stroke="#E6E6E6" strokeWidth="8" />
                  <circle cx="60" cy="60" r={R} fill="none" stroke={passed ? '#7500C0' : '#D81E1E'} strokeWidth="8"
                    strokeDasharray={`${(scorePct / 100) * C} ${C}`} strokeLinecap="round" transform="rotate(-90 60 60)" />
                </svg>
                <div className="ep-score-text"><span className="ep-score-value">{overallScore}</span><span className="ep-score-total">out of {maxScore}</span></div>
              </div>
              <div className="ep-result-score-line">
                <span className="ep-result-score-label">Score <strong>{overallScore}/{maxScore}</strong></span>
                <span className={'ep-result-badge ' + (passed ? 'pass' : 'fail')}>{statusLabel}</span>
              </div>
            </div>
            <div className="ep-result-progress">
              <p className="ep-result-headline">{headline}</p>
              <p className="ep-result-summary">{summary}</p>
              {Array.isArray(highlights) && highlights.length > 0 && (
                <>
                  <p className="ep-result-notes-title">{highlightsTitle}</p>
                  <div className="ep-result-notes">
                    {highlights.map((n, i) => (
                      <div key={i} className="ep-result-note">
                        <img src={n.type === 'improved' ? '/images/uplevelgreen.svg' : '/images/purpleInfo.svg'} alt="" width="16" height="16" onError={(e) => { e.target.style.display = 'none'; }} />
                        <div><strong>{n.title}</strong>{n.message && <p>{n.message}</p>}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Keep going */}
        {keepGoing && (
          <div className="ep-result-keepgoing">
            <img src="/images/keepgoing.svg" alt="" width="18" height="18" onError={(e) => { e.target.style.display = 'none'; }} />
            <div><strong>Keep going!</strong><p>{keepGoing}</p></div>
          </div>
        )}

        {/* Section-wise scores */}
        <div className="ep-result-section-scores">
          <h3 className="ep-result-block-title"><img src="/images/section-wiseicon.svg" alt="" width="16" height="16" onError={(e) => { e.target.style.display = 'none'; }} /> Section-wise Scores</h3>
          <p className="ep-result-block-sub">A closer look at your performance in each section</p>
          <div className="ep-result-score-table">
            <div className="ep-result-score-head"><span className="ep-result-score-sec">Section</span><span className="ep-result-score-perf">Performance</span><span className="ep-result-score-num">Score per section</span></div>
            {sectionScores.map((s, i) => (
              <div key={i} className="ep-result-score-row">
                <span className="ep-result-score-sec">{s.section}</span>
                <span className="ep-result-score-perf">
                  <span className="ep-result-score-bar"><span className="ep-result-score-fill" style={{ width: s.pct + '%' }}></span></span>
                  <span className="ep-result-score-pct">{s.pct}%</span>
                </span>
                <span className="ep-result-score-num">{s.score}/{s.max}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Learn from every question */}
        <div className="ep-result-learn">
          <h3 className="ep-result-block-title"><img src="/images/purpleInfo.svg" alt="" width="16" height="16" onError={(e) => { e.target.style.display = 'none'; }} /> Learn from Every Question</h3>
          <p className="ep-result-block-sub">Go through each question to understand what you got right and where you can improve.</p>
          <div className="ep-result-tabs">
            <button className={'ep-result-tab' + (tab === 'incorrect' ? ' active' : '')} onClick={() => setTab('incorrect')}>Incorrect</button>
            <button className={'ep-result-tab' + (tab === 'skipped' ? ' active' : '')} onClick={() => setTab('skipped')}>Skipped</button>
          </div>
          <div className="ep-result-review-list">
            {activeReview.length === 0 ? (
              <p className="ep-result-empty">No {tab} questions. Great job!</p>
            ) : (
              bySection.map((g, gi) => (
                <div key={gi} className="ep-result-review-group">
                  <h4 className="ep-result-review-section">{g.section}</h4>
                  {g.items.map((r, i) => (
                    <div key={i} className="ep-result-review-item">
                      <div className="ep-result-review-q">Question {flatNum(r)}</div>
                      {isSkipped(r) ? (
                        <div className="ep-result-review-ans skipped">
                          <img src="/images/skippedicon.svg" alt="" width="16" height="16" onError={(e) => { e.target.style.display = 'none'; }} /> Not answered
                        </div>
                      ) : (r.user_answer != null && r.user_answer !== '__skipped__') && (
                        <div className="ep-result-review-ans wrong">
                          <img src="/images/mistakeicon.svg" alt="" width="16" height="16" onError={(e) => { e.target.style.display = 'none'; }} /> {optText(r, r.user_answer)}
                        </div>
                      )}
                      {r.correct_answer != null && (
                        <div className="ep-result-review-ans right">
                          <img src="/images/Done.svg" alt="" width="18" height="18" onError={(e) => { e.target.style.display = 'none'; }} /> {optText(r, r.correct_answer)}
                        </div>
                      )}
                      {r.explanation && (
                        <div className="ep-result-review-exp">
                          <strong>Explanation</strong>
                          <p>{r.explanation}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="ep-result-footer">
          <span className="ep-result-footer-hint"><img src="/images/purpleInfo.svg" alt="" width="14" height="14" onError={(e) => { e.target.style.display = 'none'; }} /> Retake Exam Prep after learning and practicing to see your progress.</span>
          <button className="ep-btn-continue-learning" onClick={onExit}>Continue Learning</button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default JapaneseExamResult;
